/* @jsx h */
import { computed, defineComponent, h } from "vue";
import { useLocalization } from "@lemmonhq/uikit";
import { localDate, weekdayNames } from "../../shared/dates";
import type { RotaWarning } from "../engine/rules";
import { templateCode, type Templates } from "../types";
import { ShiftCard } from "./ShiftCard";
import type { Shift } from "./types";

type Props = {
  days: string[];
  shifts: Shift[];
  warnings: Map<number, RotaWarning[]>;
  templates: Templates | null;
  onOpen: (shift: Shift) => void;
  onAdd: (date: string) => void;
};

// Rows are the week's time blocks (start–end), columns are the days.
export const WeekGrid = defineComponent<Props>({
  name: "WeekGrid",
  props: ["days", "shifts", "warnings", "templates", "onOpen", "onAdd"],
  setup(props) {
    const { t } = useLocalization();
    const names = weekdayNames("short");

    const rows = computed(() => {
      const keys = new Map<string, { start: string; end: string; name: string | null }>();
      for (const s of props.shifts) {
        const k = `${s.start_time}-${s.end_time}`;
        if (!keys.has(k)) keys.set(k, { start: s.start_time, end: s.end_time, name: s.block_name });
      }
      return [...keys.entries()].sort((a, b) => a[1].start.localeCompare(b[1].start) || a[1].end.localeCompare(b[1].end));
    });

    const codeFor = (iso: string, i: number) => {
      const tplId = props.templates?.weekdays.find((w) => w.weekday === i + 1)?.template_id;
      const tpl = props.templates?.templates.find((x) => x.id === tplId);
      return tpl ? templateCode(tpl) : null;
    };

    return () => {
      const today = localDate();
      return (
        <div class="overflow-x-auto rounded-2xl border border-base-300 bg-base-100">
          <table class="w-full min-w-[980px] table-fixed border-collapse">
            <thead>
              <tr>
                <th class="w-24 border-b border-base-300 p-2" />
                {props.days.map((d, i) => {
                  const code = codeFor(d, i);
                  const date = new Date(`${d}T12:00:00`);
                  return (
                    <th key={d} class={["border-b border-base-300 p-2 text-center", d === today ? "bg-primary/10" : ""].join(" ")}>
                      <div class="text-xs font-medium uppercase text-base-content/60">{names[i]}</div>
                      <div class={["text-lg font-semibold", d === today ? "text-primary" : ""].join(" ")}>{date.getDate()}</div>
                      <div class="text-[10px] text-base-content/40">{code ?? "—"}</div>
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {rows.value.length === 0 ? (
                <tr>
                  <td colspan={8} class="p-10 text-center text-sm text-base-content/50">
                    {t("workday.rota.week_empty")}
                  </td>
                </tr>
              ) : (
                rows.value.map(([key, row]) => (
                  <tr key={key} class="align-top">
                    <td class="border-b border-r border-base-300 p-2">
                      <div class="text-sm font-semibold tabular-nums">{`${row.start}–${row.end}`}</div>
                      {row.name && <div class="text-[11px] text-base-content/50">{row.name}</div>}
                    </td>
                    {props.days.map((d) => (
                      <td key={d} class={["border-b border-base-300 p-1.5", d === today ? "bg-primary/5" : ""].join(" ")}>
                        <div class="space-y-1">
                          {props.shifts
                            .filter((s) => s.shift_date === d && `${s.start_time}-${s.end_time}` === key)
                            .map((s) => (
                              <ShiftCard key={s.id} shift={s} warnings={props.warnings.get(s.id) ?? []} onOpen={() => props.onOpen(s)} />
                            ))}
                        </div>
                      </td>
                    ))}
                  </tr>
                ))
              )}
              <tr>
                <td class="p-2 text-[11px] text-base-content/40">{t("workday.rota.add_shift")}</td>
                {props.days.map((d) => (
                  <td key={d} class="p-1.5">
                    <button
                      class="flex w-full items-center justify-center rounded-lg border border-dashed border-base-300 py-1 text-base-content/40 hover:border-primary hover:text-primary"
                      aria-label={t("workday.rota.add_shift")}
                      onClick={() => props.onAdd(d)}
                    >
                      <span class="material-symbols-outlined text-base">add</span>
                    </button>
                  </td>
                ))}
              </tr>
            </tbody>
          </table>
        </div>
      );
    };
  },
});
